import { useState, useRef, useEffect } from 'react'
import css from './SectionAddTask.module.css'
import ButtonRound from '@/components/ui/ButtonRound/ButtonRound'
import { showToast } from '@/components/app/Toast/showToast'

// Max tasks created from a single paste. Caps accidental huge pastes and keeps the batch well
// under Firestore's 500-operation transaction limit (the batch create runs in one transaction).
const MAX_PASTE_TASKS = 20

interface SectionAddTaskProps {
    onAdd: (name: string) => void
    // Create several tasks at once (paste a multi-line / bullet list). When absent, a multi-line
    // paste falls through to the default single-input behavior.
    onAddMany?: (names: string[]) => void
}

const SectionAddTask = ({ onAdd, onAddMany }: SectionAddTaskProps) => {
    const [isEditing, setIsEditing] = useState(false)
    const [value, setValue] = useState('')
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus()
        }
    }, [isEditing])

    const handleButtonClick = () => {
        setIsEditing(true)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && value.trim()) {
            onAdd(value.trim())
            setValue('')
        } else if (e.key === 'Escape') {
            setValue('')
            setIsEditing(false)
        }
    }

    const handleBlur = () => {
        if (value.trim()) {
            onAdd(value.trim())
            setValue('')
        }
        setIsEditing(false)
    }

    // Paste a bullet / numbered / multi-line list to create one task per line (SCD-897). Strips a
    // leading list marker (-, *, •, –, "1.", "1)") and an optional markdown task-list checkbox
    // ([ ], [x]) from each line. A single-line paste falls through to the default input behavior
    // so normal pasting is unchanged.
    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        if (!onAddMany) return
        const text = e.clipboardData.getData('text/plain')
        const lines = text
            .split(/\r?\n/)
            .map((line) => line.replace(/^\s*([-*•–]|\d+[.)])\s+(?:\[[ xX]?\]\s*)?/, '').replace(/->/g, '→').trim())
            .filter(Boolean)
        if (lines.length <= 1) return
        e.preventDefault()
        const capped = lines.slice(0, MAX_PASTE_TASKS)
        onAddMany(capped)
        if (lines.length > MAX_PASTE_TASKS) {
            showToast(`Pasted the first ${MAX_PASTE_TASKS} tasks (max ${MAX_PASTE_TASKS} per paste).`)
        }
        setValue('')
    }

    if (isEditing) {
        return (
            <div className={css.input_wrapper}>
                <input
                    ref={inputRef}
                    type="text"
                    className={css.input}
                    // hack for safari on ios, to prevent AutoFill Contact
                    placeholder={'Task n\u200Dame...'}
                    autoComplete="none"
                    value={value}
                    onChange={(e) => setValue(e.target.value.replace(/->/g, '→'))}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    onBlur={handleBlur}
                    data-testid="add-task-input"
                />
            </div>
        )
    }

    return (
        <div className={css.button_wrapper}>
            <ButtonRound onClick={handleButtonClick} testId="add-task-btn">Add task</ButtonRound>
        </div>
    )
}

export default SectionAddTask